import type { PropsWithChildren } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { BootSplash } from '@/components/BootSplash';
import { useGroupBootstrap } from '@/hooks/useGroupBootstrap';
import { useWalletRealtime } from '@/hooks/useWalletRealtime';
import { useAuthStore } from '@/stores/authStore';
import { useGroupContextStore } from '@/stores/groupContextStore';

/**
 * 路由守衛：未登入導去 ``/login``、bootstrap 期間顯示載入畫面。
 *
 * 行為：
 * - ``hydrating === true``：顯示 ``<BootSplash phase="auth">``，避免閃過登入頁。
 * - ``user === null``：``<Navigate to="/login" replace state={{ from }} />``，
 *   登入完成後可由 ``LoginPage`` 用 ``location.state.from`` 導回原路徑。
 * - 其餘：原樣 render children。
 *
 * **R45 chained bootstrap**：``useGroupBootstrap`` 在 auth 完成後自動 fire 一次拉
 * `GET /groups` + 算 currentGid;對齊 R45 Security Engineer panel「不可塞進
 * useAuthBootstrap body — OAuth grep 守則」拍板。
 *
 * @example
 *   <Route path="/home" element={<RequireAuth><HomePage /></RequireAuth>} />
 */
export function RequireAuth({ children }: PropsWithChildren) {
  const user = useAuthStore((s) => s.user);
  const hydrating = useAuthStore((s) => s.hydrating);
  const creativeUid = useAuthStore((s) => s.creativeUid);
  const currentGroupId = useGroupContextStore((s) => s.currentGroupId);
  const bootstrapping = useGroupContextStore((s) => s.bootstrapping);
  const location = useLocation();
  // R45 Phase 1:auth bootstrap 完成後 chain group bootstrap(hook 內部 dedupe)
  useGroupBootstrap();
  // R47.0 — wallet realtime onSnapshot 訂 `users/{cuid}/wallets/{gid}`;
  // currentGroupId / creativeUid 任一 null 時 hook no-op(useFirestoreSubscription 內 empty
  // path 自動 skip)。groupId guard 在 walletStore.setWallet 內(對齊 R45 Security panel)。
  useWalletRealtime(creativeUid, currentGroupId);

  if (hydrating) {
    return <BootSplash phase="auth" />;
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  // R48.10 — bootstrap race fix(refined):只在「沒 gid」+「bootstrap 還沒結束」雙條
  // 件下 block children render。語意:
  //   - 返回用戶(localStorage 已有 gid)→ 即時 render(bootstrap 仍 background 跑,
  //     會 refresh wallet 但不擋 UI)
  //   - 全新 user / 換裝置 / 清過 localStorage → 必 block 直到拿到 gid,避免 child
  //     page fetch L1.5 endpoint 時 X-Current-Group 還沒寫進 store 撞 400
  //     CURRENT_GROUP_REQUIRED
  //   - `bootstrapping === false` 代表 bootstrap 已結束(success / fallback / 全失敗
  //     皆放行;若全失敗仍沒 gid 走 degraded mode,各 fetch 自處 errorMap toast)
  if (!currentGroupId && bootstrapping !== false) {
    // ⚠️ 與上面同一個元件型別、同一個樹位置 → React 保留 state,`elapsedMs` 不會歸零
    // (整段冷啟動累計計時;分段重計的話 12 秒那則說明永遠不會出現)。
    return <BootSplash phase="workspace" />;
  }

  return <>{children}</>;
}
